import { apiClient } from './client'
import type { EditorJsData, Homework } from '@/types'

type ApiWrapper<T> = {
  message?: string
  data?: T
}

type IndexResponse<T> =
  | T[]
  | {
      data?: T[]
      message?: string
    }

type HomeworkShowResponse = Homework | { data?: Homework }

function isWrappedHomeworkResponse(
  raw: HomeworkShowResponse
): raw is { data?: Homework } {
  return Boolean(raw && typeof raw === 'object' && 'data' in raw)
}

function unwrapIndex<T>(raw: IndexResponse<T>): T[] {
  if (Array.isArray(raw)) return raw
  if (raw?.data && Array.isArray(raw.data)) return raw.data
  return []
}

export const homeworksApi = {
  // For teacher: list by group
  getByGroup: async (groupId: number): Promise<Homework[]> => {
    const response = await apiClient.get<IndexResponse<Homework>>(`/groups/${groupId}/homeworks`)
    return unwrapIndex(response.data)
  },

  // For teacher: list by user (student)
  getByUser: async (userId: number): Promise<Homework[]> => {
    const response = await apiClient.get<IndexResponse<Homework>>(`/users/${userId}/homeworks`)
    return unwrapIndex(response.data)
  },

  getByUserAndId: async (userId: number, homeworkId: number): Promise<Homework> => {
    const response = await apiClient.get<HomeworkShowResponse>(
      `/users/${userId}/homeworks/${homeworkId}`
    )

    if (isWrappedHomeworkResponse(response.data)) {
      if (response.data.data) return response.data.data
      throw new Error('Домашка не найдена')
    }

    return response.data
  },

  // Called after lesson finish: it creates individual homework copies for all students in lesson
  createFromLesson: async (lessonId: number, payload: { task_json: EditorJsData; student_ids?: number[] }): Promise<Homework[]> => {
    const response = await apiClient.post<ApiWrapper<Homework[]>>(`/lessons/${lessonId}/homeworks`, payload)
    // Backend returns `HomeworkResource::collection(collect($homeworks))` => usually wrapped in `data: [...]`.
    if (response.data?.data) return response.data.data
    return []
  },

  answerByUser: async (
    userId: number,
    homeworkId: number,
    payload: { student_answer_json: EditorJsData; completed_at?: string | null }
  ): Promise<Homework> => {
    const response = await apiClient.patch<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/student-answer`,
      payload
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка сохранения ответа')
  },

  markCompletedByUser: async (userId: number, homeworkId: number): Promise<Homework> => {
    const response = await apiClient.patch<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/mark-completed`,
      {}
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка отметки выполнения')
  },

  checkByUser: async (
    userId: number,
    homeworkId: number,
    payload: { action: 'approve' } | { action: 'revision'; comment: string } = { action: 'approve' }
  ): Promise<Homework> => {
    const response = await apiClient.patch<ApiWrapper<Homework>>(
      `/users/${userId}/homeworks/${homeworkId}/check`,
      payload
    )
    if (response.data?.data) return response.data.data
    throw new Error(response.data?.message || 'Ошибка проверки домашнего задания')
  },
}

